import type { MenuItem, MenuType } from '@packages/cms-components/interfaces';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { type JSX } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';

const filterAndMarkActiveMenu = (menuData: MenuItem[], currentPath: string): MenuType => {
  menuData.forEach((menuEntry) => {
    if (menuEntry.children) {
      const { active: remove, ...menuEntryChildren } = menuEntry.children;
      // eslint-disable-next-line no-param-reassign
      menuEntry.children = filterAndMarkActiveMenu(Object.values(menuEntryChildren), currentPath);
    }
    // eslint-disable-next-line no-param-reassign
    menuEntry.active =
      menuEntry.children?.active ||
      (menuEntry.is_external === false && menuEntry.alias === currentPath);
  });
  const active = Object.values(menuData).some(
    (menuChild) =>
      menuChild.children?.active ||
      (menuChild.is_external === false && menuChild.alias === currentPath),
  );
  return { active, ...menuData };
};

const RenderMenuChildren = ({ entry }: { entry: MenuItem }): JSX.Element => {
  const beforeIcon = entry.active ? '-' : '+';
  const href = entry.is_external ? entry.uri : entry.alias;
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(href);

  return (
    <li className="flex flex-row" key={entry.title}>
      <div className="mr-1 w-[0.5rem] py-1" aria-hidden="true">
        {entry.children && beforeIcon}
      </div>
      <div className="[&:has(>a:hover)]:bg-color-grey-light">
        <a
          className='text-color-text-dark inline-block py-1 pr-2 font-normal no-underline aria-[current="page"]:font-bold'
          href={finalLink}
          aria-current={entry.active ? 'page' : undefined}
          aria-expanded={entry.children ? entry.active : undefined}
        >
          {entry.title}
        </a>
        {entry.active && entry.children && (
          <ul className="flex flex-col gap-0">
            {Object.values(entry.children)
              .filter((child) => typeof child !== 'boolean')
              .map((child) => (
                <RenderMenuChildren entry={child} key={child.title} />
              ))}
          </ul>
        )}
      </div>
    </li>
  );
};

const RenderMenu = ({ menuData, currentPath }: { menuData: MenuType; currentPath: string }) => {
  const { active: remove, ...menuChildren } = menuData;
  const { active: removeFinal, ...finalMenu } = filterAndMarkActiveMenu(
    Object.values(menuChildren),
    currentPath,
  );
  return (
    <ul>
      {Object.values(finalMenu).map((entry) => (
        <RenderMenuChildren entry={entry} key={entry.title} />
      ))}
    </ul>
  );
};

const bypassNavigationMessage = defineMessage({
  id: 'service.help.navigation.skipNavigation',
  defaultMessage: 'Navigation überspringen',
});

const navLabelMessage = defineMessage({
  id: 'service.help.navigation.label',
  defaultMessage: 'Service & Hilfe',
});

export const ContentWithMenu = ({
  menuData,
  currentPath,
  children,
}: {
  menuData: MenuType;
  currentPath: string;
  children: JSX.Element;
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="flex flex-col-reverse md:flex-row">
      <BypassBlock buttonText={formatMessage(bypassNavigationMessage)}>
        <nav
          aria-label={formatMessage(navLabelMessage)}
          className="text-size-body-sm m-0 flex w-full flex-col gap-0 sm:mx-[6%] md:mx-0 md:mr-4 md:w-[13.25rem]"
        >
          <RenderMenu menuData={menuData} currentPath={currentPath} />
        </nav>
      </BypassBlock>
      <div className="sm:w-full md:w-[78%]">{children}</div>
    </div>
  );
};
